import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = auth
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return false;
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: returning from Google OAuth — AuthCallback will exchange session_id first.
    if (window.location.hash?.includes("session_id=")) {
      setChecking(false);
      return;
    }
    refresh();
  }, [refresh]);

  // Email/password admin login
  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  // Emergent managed Google sign-in.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = (redirectPath = "/") => {
    const redirectUrl = window.location.origin + redirectPath;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processGoogleSession = async (sessionId) => {
    await api.post("/auth/session", {}, { headers: { "X-Session-ID": sessionId } });
    return refresh();
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, checking, login, loginWithGoogle, processGoogleSession, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
